import axios from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  matchPath,
  NavigateFunction,
  useLocation,
  useNavigate,
  Location,
} from "react-router-dom";
import { toast } from "react-toastify";
import AuthService from "../service/authService";
import { ROUTES } from "../constant/common.constant";
import { TFunction } from "i18next";
import { useAppLoading } from "../context/AppLoading/useAppLoading";

declare module "axios" {
  export interface AxiosRequestConfig {
    retries?: number;
    retryDelay?: number;
    retry?: number;
  }
}

export function AxiosInterceptors() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setIsAppLoading } = useAppLoading();

  useEffect(() => {
    setupInterceptors(navigate, location, t, setIsAppLoading);
  }, [navigate, location, t, setIsAppLoading]);

  // not rendering anything
  return null;
}

function setupInterceptors(
  navigate: NavigateFunction,
  location: Location,
  t: TFunction,
  setIsAppLoading: (isAppLoading: boolean) => void
) {
  // Add default axios configurations
  // Only set default timeout if not already set in request config
  axios.interceptors.request.use((config) => {
    if (config.timeout === undefined) {
      config.timeout = 30000; // 30 seconds timeout
    }
    return config;
  });

  axios.defaults.retries = 3;
  axios.defaults.retryDelay = 1000; // 1 second delay between retries

  // Retry logic
  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (error) {
      const config = error.config;
      // If we should retry and we haven't reached max retries
      if (!config || !config.retry) config.retry = 0;

      const maxRetries =
        config.retries !== undefined
          ? config.retries
          : axios.defaults.retries || 3;

      if (
        config.retry < maxRetries &&
        (!error.response || error.response.status >= 500)
      ) {
        config.retry += 1;
        config.timeoutErrorMessage = `Attempt ${config.retry} of ${maxRetries}`;

        // Exponential backoff delay
        const backoff =
          Math.pow(2, config.retry) * (axios.defaults.retryDelay || 1000);
        await new Promise((resolve) => setTimeout(resolve, backoff));

        console.log(
          `Retrying request (${config.retry}/${axios.defaults.retries})`
        );
        return axios(config);
      }

      if (!error.response) {
        console.error("Please check your internet connection.");
        toast.error(t("errors.toast.connection_refused"), {
          toastId: "connection_refused",
        });
        setIsAppLoading(false);
        return Promise.reject(error);
      }

      //catches if the session ended!
      const { status } = error.response;

      //When we reset password, it's ok if the use is not valid or password expired
      if (
        status === 401 &&
        !matchPath({ path: ROUTES.RESET_PASSWORD }, location.pathname) &&
        !matchPath({ path: ROUTES.UPDATE_PASSWORD }, location.pathname)
      ) {
        console.warn("EXPIRED TOKEN!");
        navigate(ROUTES.LOGIN);
        AuthService.clearLocalUser();
      }

      if (status === 404) {
        console.error("Not found!");
      }

      if (status === 500) {
        toast.error(t("errors.toast.server_error"), {
          toastId: "server_error",
        });
        console.error("Server error!");
      }
      setIsAppLoading(false);

      return Promise.reject(error);
    }
  );
}
